import { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/Header";
import client from "@/api";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Target, Lightbulb, ClipboardCheck, Info } from "lucide-react";

export default function ChapterReader() {
  const { t, lang } = useApp();
  const { chapterId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    client.get(`/chapters/${chapterId}?lang=${lang}`).then(({ data }) => setData(data)).catch(() => toast.error(t("error")));
  }, [chapterId, lang]);

  const startTest = async () => {
    try { await client.post(`/chapters/${chapterId}/complete`); } catch (_) {}
    navigate(`/chapters/${chapterId}/quiz`);
  };

  if (!data) return <div className="min-h-screen bg-[#0d0d0d]"><Header /><div className="max-w-3xl mx-auto p-10 animate-pulse"><div className="h-10 w-2/3 bg-[#1a1a1a] rounded mb-6" /><div className="h-64 bg-[#1a1a1a] rounded" /></div></div>;

  const { content, chapter } = data;
  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <Header />
      {/* Chapter hero */}
      <div className="relative h-52 sm:h-64 overflow-hidden">
        {chapter.image_url && <img src={chapter.image_url} alt={content.title} className="w-full h-full object-cover object-center opacity-40" loading="lazy" />}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0d0d0d]/50 to-[#0d0d0d]" />
        <div className="absolute inset-0 max-w-4xl mx-auto px-4 sm:px-8 flex flex-col justify-end pb-6">
          <button data-testid="back-btn" onClick={() => navigate("/chapters")}
            className="focusable self-start flex items-center gap-1 text-sm text-[#C9CDD3] hover:text-[#D21F2A] mb-3 transition-colors">
            <ArrowLeft className="w-4 h-4" /> {t("back_to_chapters")}
          </button>
          <div className="text-xs uppercase tracking-widest text-[#D21F2A]">{chapter.area_title} · {t("source_pdf")} {chapter.pdf_pages}</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#FFFFFF] mt-1" data-testid="chapter-title">{content.title}</h1>
        </div>
      </div>

      {/* Reading surface */}
      <article className="reader-surface">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10">
          {data.language_fallback && (
            <Callout icon={Info} tone="info" testid="lang-fallback-notice">{t("lang_fallback_notice")}</Callout>
          )}

          <p className="text-lg leading-relaxed text-[#52525b] mb-8">{content.intro}</p>

          {content.learning_objectives?.length > 0 && (
            <div className="rounded-2xl border border-[#E5E5E5] bg-white p-6 mb-8">
              <div className="flex items-center gap-2 mb-3"><Target className="w-5 h-5 text-[#D21F2A]" />
                <h2 className="font-serif text-xl">{t("learning_objectives")}</h2></div>
              <ul className="space-y-2">
                {content.learning_objectives.map((o, i) => (
                  <li key={i} className="flex gap-2 text-[#141414]"><span className="text-[#D21F2A]">•</span>{o}</li>
                ))}
              </ul>
            </div>
          )}

          {content.sections?.map((s) => (
            <section key={s.section_id} id={s.section_id} className="mb-8 scroll-mt-24" data-testid={`section-${s.section_id}`}>
              <h3 className="font-serif text-2xl mb-3">{s.heading}</h3>
              <p className="leading-relaxed text-[#141414] whitespace-pre-line">{s.body}</p>
            </section>
          ))}

          {content.summary && (
            <div className="rounded-2xl bg-[#FFF5F5] border border-[#D21F2A]/40 p-6 mb-8">
              <div className="flex items-center gap-2 mb-2"><Lightbulb className="w-5 h-5 text-[#D21F2A]" />
                <h3 className="font-serif text-lg">{t("summary")}</h3></div>
              <p className="text-[#141414] leading-relaxed">{content.summary}</p>
            </div>
          )}

          {chapter.has_test && (
            <button data-testid="start-test-btn" onClick={startTest}
              className="btn-gold focusable w-full py-4 rounded-xl text-base flex items-center justify-center gap-2 mt-4">
              <ClipboardCheck className="w-5 h-5" /> {t("start_test")}
            </button>
          )}
        </div>
      </article>
    </div>
  );
}

function Callout({ icon: Icon, tone, children, testid }) {
  const tones = { warn: "bg-[#FFF4E0] border-[#E0A106] text-[#7a5300]", info: "bg-[#E7F0F8] border-[#2f6fa8] text-[#1e4b70]" };
  return (
    <div data-testid={testid} className={`flex items-start gap-2 rounded-xl border px-4 py-3 mb-6 text-sm ${tones[tone]}`}>
      <Icon className="w-4 h-4 mt-0.5 shrink-0" /> <span>{children}</span>
    </div>
  );
}
